/** Pure helpers for the sign-in and extension-pairing screens (kept DOM-free so they are unit-tested). */

/** The message the sign-in screen shows for a `?signin=` redirect from the backend, if any. */
export function signinNotice(search: string): string | null {
  const reason = new URLSearchParams(search).get('signin');
  if (reason === 'waitlisted') {
    return "Tariq is invite-only while we test it. You're on the waitlist — we'll email you when there's room.";
  }
  if (reason === 'full') {
    return "Tariq is at capacity right now. You're on the waitlist — we'll be in touch as spots open.";
  }
  return null;
}

/** Whole seconds until `expiresAtIso`, never negative. */
export function secondsLeft(expiresAtIso: string, nowMs: number): number {
  return Math.max(0, Math.ceil((Date.parse(expiresAtIso) - nowMs) / 1000));
}

/** `m:ss` countdown text. */
export function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface ConfirmedResumeFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedinUrl: string;
  githubUrl: string;
  currentLocation: string;
  targetRoles: string;
}

/** Trims the confirm-screen values, drops empty ones, and splits roles on commas or newlines. */
export function buildResumeProfilePatch(v: ConfirmedResumeFields): {
  patch: Record<string, string>;
  targetRoles: string[];
} {
  const patch: Record<string, string> = {};
  for (const key of ['firstName', 'lastName', 'email', 'phone', 'linkedinUrl', 'githubUrl', 'currentLocation'] as const) {
    const value = v[key].trim();
    if (value !== '') patch[key] = value;
  }
  const targetRoles = v.targetRoles
    .split(/[,\n]/)
    .map((r) => r.trim())
    .filter((r) => r !== '');
  return { patch, targetRoles };
}

/** A human-readable note for fields the resume did not contain, from the backend's `dropped` list. */
export function droppedNote(dropped: string[]): string | null {
  if (dropped.length === 0) return null;
  const labels = dropped.map((d) => d.replace(/^targetRoles: .*/, 'a job title').replace(/([A-Z])/g, ' $1').toLowerCase());
  return `We couldn't find ${[...new Set(labels)].join(', ')} on your resume, so we left it blank rather than guess.`;
}

export interface OnboardingState {
  hasResume: boolean;
  hasRoles: boolean;
  hasRules: boolean;
  hasHunted: boolean;
}

export interface OnboardingStep {
  key: 'resume' | 'roles' | 'rules' | 'hunt';
  label: string;
  done: boolean;
  /** Where the step's button takes the user. */
  view: 'profile' | 'rules' | 'matches';
}

/** The four first-run steps in order, or null once all are done (the card hides itself). */
export function onboardingSteps(state: OnboardingState): OnboardingStep[] | null {
  const steps: OnboardingStep[] = [
    { key: 'resume', label: 'Upload your resume', done: state.hasResume, view: 'profile' },
    { key: 'roles', label: 'Pick the roles you want', done: state.hasRoles, view: 'profile' },
    { key: 'rules', label: 'Write your rules', done: state.hasRules, view: 'rules' },
    { key: 'hunt', label: 'Run your first hunt', done: state.hasHunted, view: 'matches' },
  ];
  return steps.every((s) => s.done) ? null : steps;
}

/** A starter rules document built from the profile, for the user to edit; never empty-looking. */
export function starterRules(profile: {
  targetRoles: string[];
  locations: string[];
  dealBreakers: string[];
  salaryFloor: number | null;
  salaryCurrency: string | null;
}): string {
  const lines = ['# My rules', ''];
  if (profile.targetRoles.length > 0) {
    lines.push('## Roles I want', ...profile.targetRoles.map((r) => `- ${r}`), '');
  }
  if (profile.locations.length > 0) {
    lines.push('## Where', ...profile.locations.map((l) => `- ${l}`), '');
  }
  if (profile.salaryFloor !== null) {
    const cur = profile.salaryCurrency ? ` ${profile.salaryCurrency}` : '';
    lines.push('## Pay', `- Not below ${profile.salaryFloor.toLocaleString('en-US')}${cur}`, '');
  }
  if (profile.dealBreakers.length > 0) {
    lines.push("## Hard no's", ...profile.dealBreakers.map((d) => `- ${d}`), '');
  }
  if (lines.length === 2) lines.push('What I am looking for…', '');
  return lines.join('\n');
}

/** True when a hunt failed only because the user has not written rules yet. */
export function huntNeedsRules(message: string): boolean {
  return /filter rules document/i.test(message);
}

/** Human label + tone for an ATS support status, so the webapp never hand-rolls this mapping twice. */
export function atsStatusLabel(status: 'full' | 'partial' | 'needs_human_submit' | 'unverified'): { text: string; tone: 'good' | 'ok' | 'unknown' } {
  switch (status) {
    case 'full':
      return { text: 'Works', tone: 'good' };
    case 'partial':
      return { text: 'Mostly works', tone: 'ok' };
    case 'needs_human_submit':
      return { text: 'You finish the last step', tone: 'ok' };
    case 'unverified':
      return { text: 'Not yet verified', tone: 'unknown' };
  }
}
